/**
 * 图片导航
 */
import React, { useEffect, useState } from 'react'
import { to400Image, getGraphDataWithCursor, uniqueArray } from '~/utils'
import { AutoScroll } from '~/components/ui/AutoScroll'
import styles from './CollectionArtWorksNav.module.css'
import classNames from 'classnames'
import useInfiniteScrollCursor from '~/hooks/useInfiniteScrollCursor'
import {
  aiCollectionDetailPrivateSimple,
  aiCollectionDetailPrivateSimpleVariables,
  aiCollectionDetailPrivateSimple_aiCollectionQuery_details_aiArtworks_edges_node,
} from '~/graphqls/types'
import { AI_COLLECTION_DETAIL_PRIVATE_SIMPLE } from '~/graphqls/gqls'
import { useRouter } from 'next/router'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { MyIcon } from '~/components/ui/MyIcon'
import { message } from 'antd'

const PAGE_SIZE = 20
/** 私有合集 */
const NavigationPrivate = ({
  collectionId,
  handleClick,
  aiArtworks,
  hasNextPage,
  hasPreviousPage,
  endCursor,
  startCursor,
}: {
  [key: string]: any
}) => {
  const router = useRouter()
  const imageUrl = router.query.imageUrl
  const [count, setCount] = React.useState(0)
  const [activeKey, setActiveKey] = React.useState(-1)
  const loadingRef = React.useRef<HTMLDivElement>(null)
  const stateRef = React.useRef<any>({ offsetLeft: 0 })
  const autoScrollRef = React.useRef<any>()

  const {
    InfiniteScollComp,
    InfiniteScollPrevComp,
    list,
    hasNextPage: _hasNextPage,
    hasPrevPage: _hasPrevPage,
    onScrollPre,
    onScroll,
  } = useInfiniteScrollCursor<
    aiCollectionDetailPrivateSimple_aiCollectionQuery_details_aiArtworks_edges_node,
    aiCollectionDetailPrivateSimple,
    aiCollectionDetailPrivateSimpleVariables
  >({
    queryDoc: AI_COLLECTION_DETAIL_PRIVATE_SIMPLE,
    extraQueryParams: {
      id: collectionId,
    },
    initData: aiArtworks,
    hasNextPage: hasNextPage,
    hasPreviousPage: hasPreviousPage,
    endCursor,
    startCursor,
    initPageSize: PAGE_SIZE,
    hasNextPageExtractor: res => {
      return (
        res.data?.aiCollectionQuery?.details?.aiArtworks?.pageInfo
          ?.hasNextPage || false
      )
    },
    hasPrevPageExtractor: res => {
      return (
        res.data?.aiCollectionQuery?.details?.aiArtworks?.pageInfo
          ?.hasPreviousPage || false
      )
    },
    endCursorExtractor: res => {
      return (
        res.data.aiCollectionQuery?.details?.aiArtworks?.pageInfo?.endCursor ||
        ''
      )
    },
    startCursorExtractor: res => {
      return (
        res.data.aiCollectionQuery?.details?.aiArtworks?.pageInfo
          ?.startCursor || ''
      )
    },
    dataExtractor: res => {
      return getGraphDataWithCursor(
        res.data?.aiCollectionQuery?.details?.aiArtworks?.edges || []
      )
    },
    onFetchComplete: (dataList, direction, isClickFetch) => {
      if (isClickFetch) {
        // 左右点击发现有上一页或者下一页的时候加载数据
        if (direction === 'prev') {
          onLeftClick()
        } else if (direction === 'next') {
          onRightClick()
        }
      }
      if (direction === 'prev') {
        if (dataList?.length) {
          // 左滚动加载之后 列表会停留在第一个元素的位置 需要手动向右滚动
          setCount(count => count + 1)
        }
      }
    },
  })

  useEffect(() => {
    if (activeKey === -1 && list?.length) {
      const finded = list.find(it => it.miniArtWorkUrl === imageUrl)
      finded && setActiveKey(finded.id)
    }
  }, [list, imageUrl])

  useEffect(() => {
    if (!list?.length) {
      return
    }
    const offsetLeft = loadingRef.current?.offsetLeft!
    if (offsetLeft !== stateRef.current.offsetLeft) {
      const dist = offsetLeft - stateRef.current.offsetLeft
      const node = autoScrollRef.current.tabListContainerDom
      if (node) {
        node.scrollLeft = dist
      }
    }
    stateRef.current.offsetLeft = offsetLeft
  }, [count])
  /** 左右点击 */
  const onLeftClick = () => {
    let i = list.findIndex(item => item.miniArtWorkUrl === imageUrl)
    let currentItem
    while (i >= 0) {
      i--
      currentItem = list[i]
      const postId = currentItem?.extend?.squarePost?.id
      if (postId) {
        setActiveKey(currentItem.id)
        handleClick(postId, currentItem)
        break
      }
    }
    if (!currentItem) {
      if (_hasPrevPage) {
        onScrollPre(true)
      } else {
        message.error('没有更多了')
      }
    }
  }

  const onRightClick = () => {
    let i = list.findIndex(item => item.miniArtWorkUrl === imageUrl)
    let currentItem
    while (i < list.length) {
      i++
      currentItem = list[i]
      const postId = currentItem?.extend?.squarePost?.id
      if (postId) {
        setActiveKey(currentItem.id)
        handleClick(postId, currentItem)
        break
      }
    }
    if (!currentItem) {
      if (_hasNextPage) {
        onScroll(true)
      } else {
        message.error('没有更多了')
      }
    }
  }

  return (
    <>
      <div
        className={classNames(styles.switchArrow, 'prevButton')}
        onClick={onLeftClick}
      >
        <MyIcon
          icon="arrow-right-simple"
          className="text-[40px] font-normal text-inherit"
        />
      </div>
      <AutoScroll
        activeKey={activeKey}
        isUseDataKey
        className={styles.navigationWrap}
        ref={autoScrollRef}
        activeIndex={0}
      >
        <div
          className="mx-auto whitespace-nowrap"
          onClick={e => e.stopPropagation()}
        >
          {InfiniteScollPrevComp}
          {list.map((it, i) => {
            const postId = it.extend?.squarePost?.id
            return (
              <div
                key={it.id}
                data-key={it.id}
                className={classNames(styles.navigation, {
                  active: activeKey === it.id,
                  '!mr-0': i === list.length - 1,
                })}
                onClick={() => {
                  if (!postId) {
                    return
                  }
                  setActiveKey(it.id)
                  handleClick(postId, it)
                }}
              >
                <img
                  className="!rounded-[6px]  object-cover"
                  src={to400Image(it.miniArtWorkUrl!)}
                  alt=""
                />
                {!postId && <div className={styles.mask}>未发布</div>}
              </div>
            )
          })}
          {InfiniteScollComp}
          <div ref={loadingRef} className="h-1 inline-block align-middle" />
        </div>
      </AutoScroll>
      <div
        className={classNames(styles.switchArrow, 'nextButton')}
        onClick={onRightClick}
      >
        <MyIcon
          icon="arrow-right-simple"
          className="text-[40px] font-normal text-inherit"
        />
      </div>
    </>
  )
}

const NavigationWrap = ({}: { [key: string]: any }) => {
  const router = useRouter()
  const collectionId = Number(router.query.collectionId)
  // const collectionPostId = Number(router.query.collectionPostId)
  const [initialInfo, setInitialInfo] = useState<any>()
  const cursor = router.query.cursor
    ? decodeURIComponent(router.query.cursor as string)
    : ''
  const privateCollection = useImperativeQuery(
    AI_COLLECTION_DETAIL_PRIVATE_SIMPLE
  )

  // 私有合集
  useEffect(() => {
    if (!collectionId) {
      return
    }
    initList()
  }, [])
  const initList = async () => {
    const after = await privateCollection({
      id: collectionId,
      endCursor: cursor,
      first: PAGE_SIZE,
      // before: cursor,
      // last: 10,
    })
    const afterData = after.data?.aiCollectionQuery?.details?.aiArtworks
    let beforeData
    if (afterData.pageInfo?.hasPreviousPage) {
      const before = await privateCollection({
        id: collectionId,
        // after: cursor,
        // first: 10,
        startCursor: afterData.pageInfo.startCursor, // cursor,
        last: PAGE_SIZE,
      })
      beforeData = before.data?.aiCollectionQuery?.details?.aiArtworks
    }

    // cursor 按照startCursor 和 endCursor来的 所以当前作品肯定在列表中
    setInitialInfo({
      list: uniqueArray(
        getGraphDataWithCursor(beforeData?.edges),
        getGraphDataWithCursor(afterData?.edges)
      ),
      totalCount: afterData.pageInfo.totalCount,
      hasNextPage: afterData.pageInfo.hasNextPage,
      endCursor: afterData.pageInfo.endCursor,
      hasPreviousPage: beforeData ? beforeData.pageInfo.hasPreviousPage : false,
      startCursor: beforeData
        ? beforeData.pageInfo.startCursor
        : afterData.pageInfo.startCursor,
    })
  }

  const handleClick = (postId: number, it: any) => {
    if (!postId) {
      return
    }
    router.replace({
      pathname: `/plaza/${postId}`,
      query: {
        ...router.query,
        cursor: encodeURIComponent(it.cursor || ''),
        imageUrl: it.miniArtWorkUrl,
      },
    })
  }
  return initialInfo ? (
    <NavigationPrivate
      collectionId={collectionId}
      handleClick={handleClick}
      aiArtworks={initialInfo.list}
      hasNextPage={initialInfo.hasNextPage}
      endCursor={initialInfo.endCursor}
      hasPreviousPage={initialInfo.hasPreviousPage}
      startCursor={initialInfo.startCursor}
    />
  ) : null
}
export default NavigationWrap
